import React from "react";
import {
  Button,
  Tag,
  Space,
  Tooltip,
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  DownloadOutlined,
  CheckOutlined,
  CloseOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import {
  convertFromUSD,
  getGlobalDisplayCurrency,
  formatInDisplayCurrency,
} from "../../utils/currencyUtils";

const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

// Status cell renderer
const StatusCellRenderer = (params) => {
  const status = params.value;
  const statusConfig = {
    pending: { color: "orange", text: "Pending" },
    overdue: { color: "red", text: "Overdue" },
    paid: { color: "green", text: "Paid" },
    cancelled: { color: "default", text: "Cancelled" },
    draft: { color: "warning", text: "Draft" },
    sent: { color: "processing", text: "Sent" },
    "fully allocated": { color: "green", text: "Fully Allocated" },
    "partially allocated": { color: "blue", text: "Partially Allocated" },
    unallocated: { color: "orange", text: "Unallocated" },
  };

  const config = statusConfig[status?.toLowerCase()] || {
    color: "default",
    text: status || "Unknown",
  };
  return <Tag color={config.color}>{config.text}</Tag>;
};

const formatWithCommas = (value) => {
  const dp = getDecimalPrecision();
  return parseFloat(value).toLocaleString('en-US', { 
    minimumFractionDigits: dp, 
    maximumFractionDigits: dp 
  });
};

// Currency symbols map for display
const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$',
  CHF: 'CHF', CNY: '¥', INR: '₹', MXN: 'MX$', BRL: 'R$', KRW: '₩',
};

// Normalize currency code
const normalizeCurrency = (currency) => {
  if (!currency) return 'USD';
  const map = { '$': 'USD', '€': 'EUR', '£': 'GBP', '¥': 'JPY' };
  const normalized = String(currency).trim().toUpperCase();
  return map[normalized] || normalized;
};

// Currency formatter - display amount using GLOBAL display currency
// CRITICAL: Backend stores ALL amounts in USD after CreateInvoiceModal conversion
// The global display currency setting allows viewing all amounts in a preferred currency
// Shows swap icon when there's any currency conversion (original != display)
const CurrencyFormatter = (params) => {
  if (params.value == null || params.value === undefined) return `$${(0).toFixed(getDecimalPrecision())}`;

  // Value from backend is ALWAYS in USD
  const usdValue = parseFloat(params.value || 0);
  
  // Get original currency from row data
  const originalCurrency = normalizeCurrency(params.data?.currency);
  
  // Check for global display currency
  const { currency: displayCurrency, symbol: displaySymbol, rate } = getGlobalDisplayCurrency();
  
  // CASE 1: Global display is NOT USD - convert and show swap icon
  if (displayCurrency !== 'USD' && rate && rate > 0) {
    const convertedValue = usdValue * rate;
    const formattedConverted = formatWithCommas(convertedValue);
    const formattedUSD = formatWithCommas(usdValue);
    
    return (
      <Tooltip
        title={`Original: $${formattedUSD} (Rate: 1 USD = ${rate} ${displayCurrency})`}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <span>{displaySymbol} {formattedConverted}</span>
          <SwapOutlined style={{ color: "#1890ff", fontSize: "12px" }} />
        </span>
      </Tooltip>
    );
  }

  // CASE 2: Global display is USD, but original currency was NOT USD
  // This means the value was converted to USD from another currency at entry time
  if (displayCurrency === 'USD' && originalCurrency !== 'USD') {
    const originalSymbol = CURRENCY_SYMBOLS[originalCurrency] || originalCurrency;
    const formattedUSD = formatWithCommas(usdValue);
    
    return (
      <Tooltip
        title={`Converted from ${originalSymbol} (${originalCurrency})`}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <span>${formattedUSD}</span>
          <SwapOutlined style={{ color: "#1890ff", fontSize: "12px" }} />
        </span>
      </Tooltip>
    );
  }

  // Default: show as USD (no conversion involved)
  return `$${formatWithCommas(usdValue)}`;
};

// Payment Status cell renderer
const PaymentStatusCellRenderer = (params) => {
  const allocationStatus = params.data.allocationStatus;
  if (!allocationStatus) return <Tag color="default">Unknown</Tag>;

  return <Tag color={allocationStatus.color}>{allocationStatus.label}</Tag>;
};

// Approval Status cell renderer
const ApprovalStatusCellRenderer = (params) => {
  const status = params.value;
  const data = params.data || {};

  // Single-level approval: only management approval is needed
  const hasManagementApprover =
    (data.management_approver &&
      (typeof data.management_approver === "object"
        ? data.management_approver.id || data.management_approver.username
        : true)) ||
    data.management_approval_date;

  // Determine the actual status - TRUST the database value first
  let actualStatus = status || "draft";

  // If status is already set in database, use it (trust the backend)
  if (status === "approved" || status === "pending_management_approval" || status === "rejected") {
    actualStatus = status;
  }
  // If management approved but status isn't approved yet, upgrade to approved
  else if (hasManagementApprover && status !== "rejected") {
    actualStatus = "approved";
  }

  const statusConfig = {
    draft: { color: "orange", text: "Pending Approval" },
    pending_senior_approval: {
      color: "orange",
      text: "Pending Approval",
    },
    pending_management_approval: {
      color: "blue",
      text: "Pending Approval",
    },
    approved: { color: "green", text: "Approved" },
    rejected: { color: "red", text: "Rejected" },
  };

  const config = statusConfig[actualStatus] || {
    color: "default",
    text: actualStatus || status || "Unknown",
  };
  return <Tag color={config.color}>{config.text}</Tag>;
};

const isEligibleForApproval = (item, userRole) => {
  const status = (item.approval_status || "draft").toLowerCase();
  if (!userRole) return false;

  const role = userRole.toLowerCase();
  if (role === "management") {
    return status === "draft" || status === "pending_management_approval";
  }
  return false;
};

// Actions cell renderer for Bills
const ActionsCellRenderer = (params) => {
  const {
    handleViewInvoice,
    handleEditInvoice,
    handleDeleteInvoice,
    handleExportPDF,
    handleApproveInvoice,
    handleRejectInvoice,
    handleOpenApprovalModal,
    handleToggleBillSelection,
    selectedBillIds,
    userRole,
  } = params.context || {};

  const handleView = () => {
    if (typeof handleViewInvoice === "function") {
      handleViewInvoice(params.data.id, params.data.invoice_type);
    }
  };

  const handleEdit = () => {
    if (typeof handleEditInvoice === "function") {
      handleEditInvoice(params.data.id, params.data.invoice_type);
    }
  };

  const handleDelete = () => {
    if (typeof handleDeleteInvoice === "function") {
      handleDeleteInvoice(params.data.id, params.data);
    }
  };

  const handleExport = () => {
    if (typeof handleExportPDF === "function") {
      handleExportPDF(params.data.id);
    }
  };

  const handleApproval = (e) => {
    // Stop event propagation to prevent grid row selection
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    // Open approval modal for single item approval
    if (typeof handleOpenApprovalModal === "function") {
      handleOpenApprovalModal(params.data, "invoice");
    }
  };

  const handleReject = (e) => {
    // Stop event propagation to prevent grid row selection
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    // Open approval modal for rejection
    if (typeof handleOpenApprovalModal === "function") {
      handleOpenApprovalModal(params.data, "invoice");
    }
  };

  const handleToggleSelection = (e) => {
    // Stop event propagation to prevent grid row selection
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    // Toggle selection for bulk approval
    if (typeof handleToggleBillSelection === "function") {
      handleToggleBillSelection(params.data.id);
    }
  };

  const approvalStatus = (params.data.approval_status || "draft").toLowerCase();
  const isSelected = selectedBillIds?.includes(params.data.id) || false;

  const normalizedRole = userRole?.toLowerCase();
  const showApprovalButton =
    normalizedRole === "management" &&
    (approvalStatus === "draft" || approvalStatus === "pending_management_approval");

  return (
    <Space
      size="small"
      wrap={false}
      style={{ display: "flex", flexWrap: "nowrap" }}
    >
      <Tooltip title="View">
        <Button
          type="text"
          icon={<EyeOutlined />}
          size="small"
          onClick={handleView}
        />
      </Tooltip>
      <Tooltip title="Edit">
        <Button
          type="text"
          icon={<EditOutlined />}
          size="small"
          onClick={handleEdit}
        />
      </Tooltip>
      {showApprovalButton && (
        <>
          <Tooltip title="Approve this item">
            <Button
              type="primary"
              icon={<CheckOutlined />}
              size="small"
              onClick={handleApproval}
              style={{
                backgroundColor:
                  approvalStatus === "approved" ? "#52c41a" : "#1890ff",
                borderColor:
                  approvalStatus === "approved" ? "#52c41a" : "#1890ff",
              }}
            >
              Approve
            </Button>
          </Tooltip>
          <Tooltip title="Reject this item">
            <Button
              type="default"
              danger
              icon={<CloseOutlined />}
              size="small"
              onClick={handleReject}
            >
              Reject
            </Button>
          </Tooltip>
          <Tooltip
            title={
              isSelected
                ? "Remove from bulk selection"
                : "Add to bulk selection"
            }
          >
            <Button
              type={isSelected ? "default" : "dashed"}
              size="small"
              onClick={handleToggleSelection}
              style={{
                backgroundColor: isSelected ? "#52c41a" : "transparent",
                borderColor: isSelected ? "#52c41a" : "#d9d9d9",
              }}
            >
              {isSelected ? "✓ Selected" : "Select"}
            </Button>
          </Tooltip>
        </>
      )}
      <Tooltip title="Export PDF">
        <Button
          type="text"
          icon={<DownloadOutlined />}
          size="small"
          onClick={handleExport}
        />
      </Tooltip>
      <Tooltip title="Delete">
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          size="small"
          onClick={handleDelete}
        />
      </Tooltip>
    </Space>
  );
};

// Actions cell renderer for Credit Notes
const CreditActionsCellRenderer = (params) => {
  const {
    handleViewCredit,
    handleEditCredit,
    handleDeleteCredit,
    handleExportCreditPDF,
    handleApproveCredit,
    handleRejectCredit,
    handleOpenApprovalModal,
    handleToggleCreditSelection,
    selectedCreditIds,
    userRole,
  } = params.context || {};

  const handleView = () => {
    if (typeof handleViewCredit === "function") {
      handleViewCredit(params.data.id);
    }
  };

  const handleEdit = () => {
    if (typeof handleEditCredit === "function") {
      handleEditCredit(params.data.id);
    }
  };

  const handleDelete = () => {
    if (typeof handleDeleteCredit === "function") {
      handleDeleteCredit(params.data.id, params.data);
    }
  };

  const handleExport = () => {
    if (typeof handleExportCreditPDF === "function") {
      handleExportCreditPDF(params.data.id);
    }
  };

  const handleApprove = () => {
    if (typeof handleApproveCredit === "function") {
      handleApproveCredit(params.data.id);
    }
  };

  const handleReject = () => {
    if (typeof handleRejectCredit === "function") {
      handleRejectCredit(params.data.id);
    }
  };

  const handleApproval = (e) => {
    // Stop event propagation to prevent grid row selection
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    // Open approval modal for single item approval
    if (typeof handleOpenApprovalModal === "function") {
      handleOpenApprovalModal(params.data, "credit");
    }
  };

  const handleRejectCreditClick = (e) => {
    // Stop event propagation to prevent grid row selection
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    // Open approval modal for rejection
    if (typeof handleOpenApprovalModal === "function") {
      handleOpenApprovalModal(params.data, "credit");
    }
  };

  const handleToggleSelection = (e) => {
    // Stop event propagation to prevent grid row selection
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    // Toggle selection for bulk approval
    if (typeof handleToggleCreditSelection === "function") {
      handleToggleCreditSelection(params.data.id);
    }
  };

  const approvalStatus = (params.data.approval_status || "draft").toLowerCase();
  const isSelected = selectedCreditIds?.includes(params.data.id) || false;

  const normalizedRole = userRole?.toLowerCase();
  const showApprovalButton =
    normalizedRole === "management" &&
    (approvalStatus === "draft" || approvalStatus === "pending_management_approval");

  return (
    <Space
      size="small"
      wrap={false}
      style={{ display: "flex", flexWrap: "nowrap" }}
    >
      <Tooltip title="View">
        <Button
          type="text"
          icon={<EyeOutlined />}
          size="small"
          onClick={handleView}
        />
      </Tooltip>
      <Tooltip title="Edit">
        <Button
          type="text"
          icon={<EditOutlined />}
          size="small"
          onClick={handleEdit}
        />
      </Tooltip>
      {showApprovalButton && (
        <>
          <Tooltip title="Approve this item">
            <Button
              type="primary"
              icon={<CheckOutlined />}
              size="small"
              onClick={handleApproval}
              style={{
                backgroundColor:
                  approvalStatus === "approved" ? "#52c41a" : "#1890ff",
                borderColor:
                  approvalStatus === "approved" ? "#52c41a" : "#1890ff",
              }}
            >
              Approve
            </Button>
          </Tooltip>
          <Tooltip title="Reject this item">
            <Button
              type="default"
              danger
              icon={<CloseOutlined />}
              size="small"
              onClick={handleRejectCreditClick}
            >
              Reject
            </Button>
          </Tooltip>
          <Tooltip
            title={
              isSelected
                ? "Remove from bulk selection"
                : "Add to bulk selection"
            }
          >
            <Button
              type={isSelected ? "default" : "dashed"}
              size="small"
              onClick={handleToggleSelection}
              style={{
                backgroundColor: isSelected ? "#52c41a" : "transparent",
                borderColor: isSelected ? "#52c41a" : "#d9d9d9",
              }}
            >
              {isSelected ? "✓ Selected" : "Select"}
            </Button>
          </Tooltip>
        </>
      )}
      <Tooltip title="Export PDF">
        <Button
          type="text"
          icon={<DownloadOutlined />}
          size="small"
          onClick={handleExport}
        />
      </Tooltip>
      <Tooltip title="Delete">
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          size="small"
          onClick={handleDelete}
        />
      </Tooltip>
    </Space>
  );
};

// Date formatter with better styling
const DateFormatter = (params) => {
  if (!params.value) return <span style={{ color: "#999" }}>N/A</span>;
  const date = new Date(params.value);
  const isOverdue = date < new Date();

  return (
    <span
      style={{
        color: isOverdue ? "#ff4d4f" : "#1890ff",
        fontWeight: isOverdue ? "bold" : "normal",
      }}
    >
      {date.toLocaleDateString()}
    </span>
  );
};

// Helper function to check if there are any approvable items
const hasApprovableItems = (data, userRole) => {
  if (!data || !Array.isArray(data) || data.length === 0) return false;
  if (!userRole) return false;

  const normalizedRole = userRole.toLowerCase();
  if (normalizedRole !== "management") {
    return false;
  }

  return data.some((item) => {
    const status = (item.approval_status || "draft").toLowerCase();
    return status === "draft" || status === "pending_management_approval";
  });
};

// Bill columns - matching initial invoices module structure
export const getBillColumns = (
  filterValues = {},
  userRole = null,
  data = []
) => [
  {
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    checkboxSelection: true,
    width: 50,
    maxWidth: 50,
    pinned: "left",
    lockPosition: true,
    suppressMenu: true,
    sortable: false,
    filter: false,
    resizable: false,
  },
  {
    headerName: "Number",
    field: "number",
    width: 120,
    sortable: true,
    filter: true,
    editable: false,
    resizable: true,
  },
  {
    headerName: "Client",
    field: "clientName",
    width: 150,
    sortable: true,
    filter: "agSetColumnFilter",
    filterParams: {
      suppressSorting: false,
    },
    editable: false,
    resizable: true,
    cellRenderer: (params) => {
      const client = params.value;
      if (!client) return "N/A";
      return client;
    },
  },
  {
    headerName: "Brand",
    field: "brandName",
    width: 150,
    sortable: true,
    filter: "agSetColumnFilter",
    filterParams: {
      suppressSorting: false,
    },
    editable: false,
    resizable: true,
    cellRenderer: (params) => {
      const brand = params.value;
      if (!brand) return "N/A";
      return brand;
    },
  },
  {
    headerName: "Subtotal",
    field: "subtotal",
    width: 120,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellRenderer: CurrencyFormatter,
    cellEditor: "agNumberCellEditor",
    cellEditorParams: {
      min: 0,
      precision: getDecimalPrecision(),
    },
    resizable: true,
  },
  {
    headerName: "Discount",
    field: "discount_amount",
    width: 120,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellRenderer: CurrencyFormatter,
    cellEditor: "agNumberCellEditor",
    cellEditorParams: {
      min: 0,
      precision: getDecimalPrecision(),
    },
    resizable: true,
  },
  {
    headerName: "Tax Rate",
    field: "tax_rate",
    width: 100,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellRenderer: (params) => {
      const value = params.value;
      return value !== undefined && value !== null
        ? `${parseFloat(value).toFixed(2)}%`
        : "0.00%";
    },
    cellEditor: "agNumberCellEditor",
    cellEditorParams: {
      min: 0,
      precision: 2,
    },
    resizable: true,
  },
  {
    headerName: "Total",
    field: "total",
    width: 120,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellRenderer: CurrencyFormatter,
    cellEditor: "agNumberCellEditor",
    cellEditorParams: {
      min: 0,
      precision: getDecimalPrecision(),
    },
    resizable: true,
  },
  {
    headerName: "Vendor",
    field: "partner",
    width: 200,
    sortable: true,
    filter: true,
    editable: false,
    resizable: true,
    // Safety cellRenderer - handle objects, corrupted strings "[object Object]", etc.
    cellRenderer: (params) => {
      const partner = params.value;
      if (!partner) return "Unknown";
      if (typeof partner === "string") {
        if (partner === "[object Object]" || partner.includes("[object")) {
          return "Unknown";
        }
        return partner;
      }
      if (typeof partner === "object") {
        return partner.name || partner.partner_name || "Unknown";
      }
      return "Unknown";
    },
  },
  {
    headerName: "Items Count",
    field: "items_count",
    width: 100,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellRenderer: (params) => params.value || 0,
    cellEditor: "agNumberCellEditor",
    cellEditorParams: {
      min: 0,
      precision: 0,
    },
    resizable: true,
  },
  {
    headerName: "Issue Date",
    field: "issueDate",
    width: 120,
    sortable: true,
    filter: "agDateColumnFilter",
    cellEditor: "agDateCellEditor",
    resizable: true,
  },
  {
    headerName: "Due Date",
    field: "dueDate",
    width: 120,
    sortable: true,
    filter: "agDateColumnFilter",
    cellEditor: "agDateCellEditor",
    resizable: true,
  },
  {
    headerName: "Paid",
    field: "allocated_amount",
    width: 120,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellRenderer: CurrencyFormatter,
    cellEditor: "agNumberCellEditor",
    cellEditorParams: {
      min: 0,
      precision: getDecimalPrecision(),
    },
    resizable: true,
  },
  {
    headerName: "Paid Percentage",
    field: "percentPaid",
    width: 130,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellRenderer: (params) => {
      const percent = params.value;
      return percent !== undefined && percent !== null
        ? `${percent.toFixed(2)}%`
        : "0.00%";
    },
    cellEditor: "agNumberCellEditor",
    cellEditorParams: {
      min: 0,
      max: 100,
      precision: 2,
    },
    resizable: true,
  },
  {
    headerName: "Payments",
    field: "allocations",
    width: 120,
    sortable: false,
    filter: false,
    cellRenderer: (params) => {
      const allocations = params.data.allocations || [];
      const { handleViewPayments } = params.context || {};

      if (!allocations || allocations.length === 0) {
        return <span>No payments</span>;
      }

      return (
        <Button
          type="primary"
          size="small"
          icon={<DollarOutlined />}
          onClick={() => {
            // Always show payments modal first - user can click links from there to navigate
            if (handleViewPayments) {
              handleViewPayments(params.data);
            }
          }}
        >
          {allocations.length} Payment{allocations.length > 1 ? "s" : ""}
        </Button>
      );
    },
    editable: false,
    resizable: true,
  },
  {
    headerName: "Payment Status",
    field: "allocationStatus",
    width: 130,
    sortable: true,
    filter: "agSetColumnFilter",
    filterParams: {
      values: ["Fully Paid", "Partially Paid", "Unpaid"],
      suppressSorting: true,
    },
    // CRITICAL: Extract the label string from allocationStatus object for filtering
    filterValueGetter: (params) => {
      const status = params.data?.allocationStatus;
      if (!status) return "Unknown";
      if (typeof status === "string") return status;
      return status.label || "Unknown";
    },
    cellRenderer: PaymentStatusCellRenderer,
    editable: false,
    cellEditor: "agSelectCellEditor",
    cellEditorParams: {
      values: ["Fully Paid", "Partially Paid", "Unpaid"],
    },
    resizable: true,
  },
  {
    headerName: "Status",
    field: "status",
    width: 100,
    sortable: true,
    filter: true,
    cellRenderer: StatusCellRenderer,
    cellEditor: "agSelectCellEditor",
    cellEditorParams: {
      values: ["paid", "overdue", "cancelled", "draft", "sent"],
    },
    resizable: true,
  },
  {
    headerName: "Approval Status",
    field: "approval_status",
    width: 180,
    sortable: true,
    filter: "agSetColumnFilter",
    filterParams: {
      values: ["draft", "pending_management_approval", "approved", "rejected"],
      valueFormatter: (params) => {
        const statusLabels = {
          draft: "Draft - Waiting Senior Approval",
          pending_management_approval: "Waiting Management Approval",
          approved: "Approved",
          rejected: "Rejected",
        };
        return statusLabels[params.value] || params.value;
      },
      suppressSorting: true,
    },
    cellRenderer: ApprovalStatusCellRenderer,
    editable: false,
    resizable: true,
  },
  {
    headerName: "Last Modified By",
    field: "last_modified_by",
    width: 150,
    sortable: true,
    filter: true,
    cellRenderer: (params) => params.value || "Unknown",
    editable: false,
    resizable: true,
  },
  {
    headerName: "Actions",
    field: "actions",
    width: hasApprovableItems(data, userRole) ? 380 : 200,
    pinned: "right",
    cellRenderer: ActionsCellRenderer,
    sortable: false,
    filter: false,
    editable: false,
    resizable: true,
    minWidth: hasApprovableItems(data, userRole) ? 380 : 200,
  },
];

// Credit Notes columns - matching initial invoices module structure
export const getCreditNoteColumns = (
  filterValues = {},
  userRole = null,
  data = []
) => [
  {
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    checkboxSelection: true,
    width: 50,
    maxWidth: 50,
    pinned: "left",
    lockPosition: true,
    suppressMenu: true,
    sortable: false,
    filter: false,
    resizable: false,
  },
  {
    headerName: "Number",
    field: "number",
    width: 120,
    sortable: true,
    filter: true,
    editable: false,
    resizable: true,
  },
  {
    headerName: "Client",
    field: "clientName",
    width: 150,
    sortable: true,
    filter: "agSetColumnFilter",
    filterParams: {
      suppressSorting: false,
    },
    editable: false,
    resizable: true,
    cellRenderer: (params) => {
      const client = params.value;
      if (!client) return "N/A";
      return client;
    },
  },
  {
    headerName: "Brand",
    field: "brandName",
    width: 150,
    sortable: true,
    filter: "agSetColumnFilter",
    filterParams: {
      suppressSorting: false,
    },
    editable: false,
    resizable: true,
    cellRenderer: (params) => {
      const brand = params.value;
      if (!brand) return "N/A";
      return brand;
    },
  },
  {
    headerName: "Subtotal",
    field: "subtotal",
    width: 120,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellRenderer: CurrencyFormatter,
    cellEditor: "agNumberCellEditor",
    cellEditorParams: {
      min: 0,
      precision: getDecimalPrecision(),
    },
    resizable: true,
  },
  {
    headerName: "Discount",
    field: "discount_amount",
    width: 120,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellRenderer: CurrencyFormatter,
    cellEditor: "agNumberCellEditor",
    cellEditorParams: {
      min: 0,
      precision: getDecimalPrecision(),
    },
    resizable: true,
  },
  {
    headerName: "Tax Rate",
    field: "tax_rate",
    width: 100,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellRenderer: (params) => {
      const value = params.value;
      return value !== undefined && value !== null
        ? `${parseFloat(value).toFixed(2)}%`
        : "0.00%";
    },
    cellEditor: "agNumberCellEditor",
    cellEditorParams: {
      min: 0,
      precision: 2,
    },
    resizable: true,
  },
  {
    headerName: "Total",
    field: "total",
    width: 120,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellRenderer: CurrencyFormatter,
    cellEditor: "agNumberCellEditor",
    cellEditorParams: {
      min: 0,
      precision: getDecimalPrecision(),
    },
    resizable: true,
  },
  {
    headerName: "Vendor",
    field: "partner",
    width: 200,
    sortable: true,
    filter: true,
    editable: false,
    resizable: true,
    // Safety cellRenderer - handle objects, corrupted strings "[object Object]", etc.
    cellRenderer: (params) => {
      const partner = params.value;
      if (!partner) return "Unknown";
      if (typeof partner === "string") {
        if (partner === "[object Object]" || partner.includes("[object")) {
          return "Unknown";
        }
        return partner;
      }
      if (typeof partner === "object") {
        return partner.name || partner.partner_name || "Unknown";
      }
      return "Unknown";
    },
  },
  {
    headerName: "Items Count",
    field: "items_count",
    width: 100,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellRenderer: (params) => params.value || 0,
    cellEditor: "agNumberCellEditor",
    cellEditorParams: {
      min: 0,
      precision: 0,
    },
    resizable: true,
  },
  {
    headerName: "Issue Date",
    field: "issueDate",
    width: 120,
    sortable: true,
    filter: "agDateColumnFilter",
    cellEditor: "agDateCellEditor",
    resizable: true,
  },
  {
    headerName: "Due Date",
    field: "dueDate",
    width: 120,
    sortable: true,
    filter: "agDateColumnFilter",
    cellEditor: "agDateCellEditor",
    resizable: true,
  },
  {
    headerName: "Allocated",
    field: "allocated_amount",
    width: 120,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellRenderer: CurrencyFormatter,
    cellEditor: "agNumberCellEditor",
    cellEditorParams: {
      min: 0,
      precision: getDecimalPrecision(),
    },
    resizable: true,
  },
  {
    headerName: "Remaining Amount",
    field: "remainingAmount",
    width: 130,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellRenderer: CurrencyFormatter,
    editable: false,
    resizable: true,
  },
  {
    headerName: "Payments",
    field: "allocations",
    width: 120,
    sortable: false,
    filter: false,
    cellRenderer: (params) => {
      const allocations = params.data.allocations || [];
      const allocatedAmount = parseFloat(params.data.allocated_amount || 0);
      const { handleViewPayments } = params.context || {};

      // Show "View Allocations" if allocated_amount > 0, even if allocations array is empty
      if (allocatedAmount > 0 && (!allocations || allocations.length === 0)) {
        return (
          <Button
            type="primary"
            size="small"
            icon={<DollarOutlined />}
            onClick={() => {
              if (handleViewPayments) {
                handleViewPayments(params.data);
              }
            }}
          >
            View Allocations
          </Button>
        );
      }

      if (!allocations || allocations.length === 0) {
        return <span>No allocations</span>;
      }

      return (
        <Button
          type="primary"
          size="small"
          icon={<DollarOutlined />}
          onClick={() => {
            if (handleViewPayments) {
              handleViewPayments(params.data);
            }
          }}
        >
          {allocations.length} Allocation{allocations.length > 1 ? "s" : ""}
        </Button>
      );
    },
    editable: false,
    resizable: true,
  },
  {
    headerName: "Allocation Status",
    field: "allocationStatus",
    width: 130,
    sortable: true,
    filter: "agSetColumnFilter",
    filterParams: {
      values: ["Fully Allocated", "Partially Allocated", "Unallocated"],
      suppressSorting: true,
    },
    // CRITICAL: Extract the label string from allocationStatus object for filtering
    filterValueGetter: (params) => {
      const status = params.data?.allocationStatus;
      if (!status) return "Unknown";
      if (typeof status === "string") return status;
      return status.label || "Unknown";
    },
    cellRenderer: PaymentStatusCellRenderer,
    editable: false,
    cellEditor: "agSelectCellEditor",
    cellEditorParams: {
      values: ["Fully Allocated", "Partially Allocated", "Unallocated"],
    },
    resizable: true,
  },
  {
    headerName: "Status",
    field: "status",
    width: 100,
    sortable: true,
    filter: true,
    cellRenderer: StatusCellRenderer,
    cellEditor: "agSelectCellEditor",
    cellEditorParams: {
      values: ["Fully Allocated", "Partially Allocated", "Unallocated"],
    },
    resizable: true,
  },
  {
    headerName: "Approval Status",
    field: "approval_status",
    width: 180,
    sortable: true,
    filter: "agSetColumnFilter",
    filterParams: {
      values: ["draft", "pending_management_approval", "approved", "rejected"],
      valueFormatter: (params) => {
        const statusLabels = {
          draft: "Draft - Waiting Senior Approval",
          pending_management_approval: "Waiting Management Approval",
          approved: "Approved",
          rejected: "Rejected",
        };
        return statusLabels[params.value] || params.value;
      },
      suppressSorting: true,
    },
    cellRenderer: ApprovalStatusCellRenderer,
    editable: false,
    resizable: true,
  },
  {
    headerName: "Last Modified By",
    field: "last_modified_by",
    width: 150,
    sortable: true,
    filter: true,
    cellRenderer: (params) => params.value || "Unknown",
    editable: false,
    resizable: true,
  },
  {
    headerName: "Actions",
    field: "actions",
    width: hasApprovableItems(data, userRole) ? 380 : 200,
    pinned: "right",
    cellRenderer: CreditActionsCellRenderer,
    sortable: false,
    filter: false,
    editable: false,
    resizable: true,
    minWidth: hasApprovableItems(data, userRole) ? 380 : 200,
  },
];

// History columns
export const getHistoryColumns = () => [
  {
    headerName: "S.L",
    field: "sl",
    width: 80,
    resizable: true,
    sortable: true,
    filter: false,
    editable: false,
  },
  {
    headerName: "Bill Date",
    field: "billDate",
    width: 150,
    resizable: true,
    sortable: true,
    filter: true,
    editable: false,
  },
  {
    headerName: "Vendor",
    field: "vendor",
    width: 200,
    resizable: true,
    sortable: true,
    filter: true,
    editable: false,
  },
  {
    headerName: "Bill Number",
    field: "billNumber",
    width: 150,
    resizable: true,
    sortable: true,
    filter: true,
    editable: false,
  },
  {
    headerName: "Amount",
    field: "amount",
    width: 150,
    resizable: true,
    sortable: true,
    filter: "agNumberColumnFilter",
    cellRenderer: CurrencyFormatter,
    editable: false,
  },
  {
    headerName: "Due Date",
    field: "dueDate",
    width: 150,
    resizable: true,
    sortable: true,
    filter: true,
    editable: false,
  },
  {
    headerName: "Status",
    field: "status",
    width: 100,
    resizable: true,
    sortable: true,
    filter: true,
    cellRenderer: StatusCellRenderer,
    editable: false,
  },
  {
    headerName: "Description",
    field: "description",
    width: 200,
    resizable: true,
    sortable: true,
    filter: true,
    editable: false,
  },
  {
    headerName: "Created At",
    field: "createdAt",
    width: 180,
    resizable: true,
    sortable: true,
    filter: true,
    editable: false,
  },
];
